// src/components/Users.js (or src/pages/Users.js depending on your structure)
import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from './services/api';
import { useAuth } from './contexts/AuthContext'; // CHANGE: Import Auth Context
import UserList from './parts/UserList';
import UserForm from './widgets/UserForm';
import Modal from './parts/Modal';
import OrgAssignmentModal from './widgets/OrgAssignmentModal'; 
import './style/PageStyle/Users.css';

const Users = () => {
  // --- Auth State ---
  const { user } = useAuth(); // CHANGE: Get current user
  const currentUserRole = user?.role_key || user?.role; // CHANGE: Derive role

  // --- Data State ---
  const [users, setUsers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // --- UI State ---
  const [showModal, setShowModal] = useState(false);
  
  // --- Assignment Modal State ---
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUserForAssign, setSelectedUserForAssign] = useState(null);

  // --- Filter States ---
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // --- Fetch Data ---
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const [usersData, orgsData] = await Promise.all([
        apiService.getUsers(),
        apiService.getOrganisations()
      ]);

      const safeUsers = Array.isArray(usersData) ? usersData : [];
      const safeOrgs = Array.isArray(orgsData) ? orgsData : [];

      setUsers(safeUsers);
      setOrganisations(safeOrgs);

      const uniqueRoles = [...new Set(safeUsers.map(u => u.role).filter(Boolean))];
      const uniqueStatuses = [...new Set(safeUsers.map(u => u.status).filter(Boolean))];

      setAvailableRoles(uniqueRoles.sort());
      setAvailableStatuses(uniqueStatuses.sort());

    } catch (error) {
      console.error("Failed to load user data", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUserCreated = () => {
    setShowModal(false);
    loadData(); 
  };

  // --- Handlers for Assignment ---
  const handleAssignClick = (user) => {
    setSelectedUserForAssign(user);
    setShowAssignModal(true);
  };

  const handleAssignSave = () => {
    setShowAssignModal(false);
    setSelectedUserForAssign(null);
  };

  return (
    <div className="users">
      <div className="users-header">
        <h1>User Management</h1>
        <button 
          className="user-btn-primary"
          onClick={() => setShowModal(true)}
        >
          + Add User
        </button>
      </div>

      {/* --- Filter Section --- */}
      <div className="filter-bar">
        <div className="filter-group search-group">
          <input 
            type="text" 
            placeholder="Search users..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <select 
            value={selectedOrg} 
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="filter-select"
          >
            <option value="">All Organisations</option>
            {organisations.map(org => (
              <option key={org.id} value={org.name}>{org.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select 
            value={selectedRole} 
            onChange={(e) => setSelectedRole(e.target.value)}
            className="filter-select"
          >
            <option value="">All Roles</option>
            {availableRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            {availableStatuses.map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* CHANGE: Passed organisations and currentUserRole */}
      <UserList 
        users={users}
        organisations={organisations} 
        isLoading={isLoading}
        onRefresh={loadData}
        onAssignOrgs={handleAssignClick}
        currentUserRole={currentUserRole}
        filters={{
          search: searchTerm,
          org: selectedOrg,
          role: selectedRole,
          status: selectedStatus
        }}
      />

      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <UserForm onUserCreated={handleUserCreated} />
      </Modal>

      {showAssignModal && selectedUserForAssign && (
        <OrgAssignmentModal 
          user={selectedUserForAssign}
          allOrganisations={organisations}
          onClose={() => setShowAssignModal(false)}
          onSave={handleAssignSave}
        />
      )}
    </div>
  );
};

export default Users;
